import React, { useEffect, useMemo, useRef } from 'react';

/**
 * Minimal, dependency-free rich-text editor built on `contenteditable`.
 *
 * We deliberately expose just the formatting options a funeral director is
 * likely to need for an obituary: bold, italic, underline, bulleted /
 * numbered lists, and a way to split paragraphs. Headings and colors are
 * intentionally omitted — the branded PDF template chooses the visual style
 * for us and we don't want editors introducing tone clashes.
 *
 * The editor is uncontrolled internally (we only seed it once and write back
 * via `onChange`) so the caret does not jump around while typing.
 *
 * Props:
 *   initialHtml  – HTML to populate the editor with on mount / when `seedKey`
 *                  changes.
 *   initialText  – fallback plain text if `initialHtml` is empty. Paragraph
 *                  breaks are converted to <p> blocks.
 *   seedKey      – change this value to force the editor to re-seed (useful
 *                  when opening the editor for a different record).
 *   onChange     – called with the current HTML after every edit.
 *   placeholder  – shown when the field is empty.
 *   disabled     – read-only mode.
 *   minHeight    – CSS min-height for the editor surface (default 240px).
 */
export default function RichTextEditor({
  initialHtml = '',
  initialText = '',
  seedKey = 0,
  onChange,
  placeholder = 'Write here…',
  disabled = false,
  minHeight = 240,
}) {
  const editorRef = useRef(null);
  const lastSeedRef = useRef(null);

  const seededHtml = useMemo(() => {
    if (initialHtml && initialHtml.trim()) return initialHtml;
    return textToHtml(initialText || '');
  }, [initialHtml, initialText]);

  // Seed the editor content once, or whenever `seedKey` changes. We avoid
  // re-setting innerHTML on every render because doing so would clobber the
  // caret position while the user types.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (lastSeedRef.current === seedKey) return;
    lastSeedRef.current = seedKey;
    el.innerHTML = seededHtml || '';
  }, [seedKey, seededHtml]);

  const emit = () => {
    if (!onChange || !editorRef.current) return;
    onChange(editorRef.current.innerHTML);
  };

  const exec = (cmd, value = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    try {
      document.execCommand(cmd, false, value);
    } catch (_) {
      /* some browsers throw for unsupported commands; ignore */
    }
    emit();
  };

  const handleKeyDown = (e) => {
    // Keep paragraphs as <p> blocks. Chrome defaults to <div>, which is ugly
    // and hard to style; Firefox already uses <p>. We don't force it on
    // every keystroke because doing so would break list handling, but we do
    // handle a few common keyboard shortcuts ourselves so the toolbar and
    // keys stay in sync.
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === 'b') {
      e.preventDefault();
      exec('bold');
    } else if (k === 'i') {
      e.preventDefault();
      exec('italic');
    } else if (k === 'u') {
      e.preventDefault();
      exec('underline');
    }
  };

  const handlePaste = (e) => {
    // Strip formatting on paste. The source is frequently Word / Google Docs
    // with inline styles that wreck the obituary layout; we want the text
    // content only, preserving paragraph breaks.
    if (disabled) return;
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    const html = textToHtml(text);
    // insertHTML is deprecated but still universally supported and is the
    // only sane way to inject at the caret without killing the undo stack.
    document.execCommand('insertHTML', false, html);
    emit();
  };

  return (
    <div className={`rte ${disabled ? 'is-disabled' : ''}`}>
      <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
        <ToolbarButton title="Bold (Ctrl/Cmd+B)" onClick={() => exec('bold')} disabled={disabled}>
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton title="Italic (Ctrl/Cmd+I)" onClick={() => exec('italic')} disabled={disabled}>
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton title="Underline (Ctrl/Cmd+U)" onClick={() => exec('underline')} disabled={disabled}>
          <span style={{ textDecoration: 'underline' }}>U</span>
        </ToolbarButton>
        <span className="rte-sep" aria-hidden="true" />
        <ToolbarButton title="Bulleted list" onClick={() => exec('insertUnorderedList')} disabled={disabled}>
          •&nbsp;List
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => exec('insertOrderedList')} disabled={disabled}>
          1.&nbsp;List
        </ToolbarButton>
        <span className="rte-sep" aria-hidden="true" />
        <ToolbarButton title="New paragraph" onClick={() => exec('formatBlock', 'P')} disabled={disabled}>
          ¶
        </ToolbarButton>
        <ToolbarButton title="Clear formatting" onClick={() => exec('removeFormat')} disabled={disabled}>
          Clear
        </ToolbarButton>
      </div>
      <div
        ref={editorRef}
        className="rte-surface"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        style={{ minHeight }}
        role="textbox"
        aria-multiline="true"
        spellCheck
      />
      <style>{`
        .rte {
          border: 1px solid #d0d7de;
          border-radius: 10px;
          background: #fff;
          overflow: hidden;
        }
        .rte.is-disabled { opacity: 0.6; }
        .rte-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          padding: 6px 8px;
          background: #f6f8fa;
          border-bottom: 1px solid #e5e7eb;
          align-items: center;
        }
        .rte-sep {
          width: 1px;
          height: 20px;
          background: #d0d7de;
          margin: 0 4px;
        }
        .rte-toolbar button {
          border: 1px solid transparent;
          background: transparent;
          padding: 4px 9px;
          border-radius: 6px;
          font-size: 13px;
          color: #24292f;
          cursor: pointer;
          line-height: 1.2;
        }
        .rte-toolbar button:hover:not(:disabled) {
          background: #fff;
          border-color: #d0d7de;
        }
        .rte-toolbar button:disabled { cursor: not-allowed; opacity: 0.5; }
        .rte-surface {
          padding: 16px 18px;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 15px;
          line-height: 1.7;
          color: #1f2328;
          outline: none;
          white-space: normal;
        }
        .rte-surface:empty::before {
          content: attr(data-placeholder);
          color: #8b949e;
          pointer-events: none;
        }
        .rte-surface p { margin: 0 0 1em; }
        .rte-surface p:last-child { margin-bottom: 0; }
        .rte-surface ul, .rte-surface ol { padding-left: 1.4em; margin: 0 0 1em; }
        .rte-surface li { margin-bottom: 0.25em; }
        .rte-surface:focus { box-shadow: inset 0 0 0 2px rgba(37, 99, 235, 0.18); }
      `}</style>
    </div>
  );
}

function ToolbarButton({ title, onClick, disabled, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/**
 * Convert a plain-text obituary (newline-separated paragraphs) to the HTML
 * the editor expects. Lone newlines become <br>; blank lines between
 * paragraphs become new <p> blocks.
 */
function textToHtml(text) {
  const safe = (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paragraphs = safe
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!paragraphs.length) return '';
  return paragraphs
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}
